import { EmbedLayout } from '@/components/layout';
import { ExperienceList, SearchBar, CategoryFilter, StayDateHeader } from '@/components/experiences';
import { useExperiences } from '@/hooks/useExperiences';
import { usePropertyContext, useWidgetSettings } from '@/context/PropertyContext';
import { useStayContext, useDuringStayMode } from '@/context/StayContext';

/**
 * Embed page - entry point for iframe widget
 * Displays experience list with minimal chrome
 * 
 * Supports "during-stay" mode which:
 * - Shows a header with stay dates and countdown
 * - Filters experiences to only those available during the stay
 * - Provides UI to change stay dates dynamically
 */
export function Embed() {
  const { isLoading: configLoading, error: configError } = usePropertyContext();
  const settings = useWidgetSettings();
  const { experiences, isLoading, error, isFilteredByStay } = useExperiences();
  const { isDuringStayMode, daysUntilStayEnds } = useDuringStayMode();
  const { mode, setStayDates, setMode } = useStayContext();

  // Handle date changes from the header
  const handleDatesChange = (startDate: string | null, endDate: string | null) => {
    setStayDates(startDate, endDate);
    // If dates are cleared, switch back to browse mode
    if (!startDate || !endDate) {
      setMode('browse');
    } else {
      setMode('during-stay');
    }
  };

  if (configLoading) {
    return (
      <EmbedLayout>
        <LoadingState />
      </EmbedLayout>
    );
  }

  if (configError) {
    return (
      <EmbedLayout>
        <ErrorState message={configError} />
      </EmbedLayout>
    );
  }

  return (
    <EmbedLayout>
      {/* Stay Date Header - shown in during-stay mode */}
      {(isDuringStayMode || mode === 'during-stay') && (
        <StayDateHeader
          className="mb-6"
          showDatePicker={true}
          onDatesChange={handleDatesChange}
        />
      )}

      {/* Filters */}
      <div className="mb-6 space-y-4">
        {settings.enableSearch && <SearchBar className="max-w-md" />}
        {settings.enableCategoryFilter && <CategoryFilter />}
      </div>

      {/* Error State */}
      {error && <ErrorState message={error} />}

      {/* No Results State for during-stay mode */}
      {!isLoading && !error && experiences.length === 0 && isFilteredByStay && (
        <NoExperiencesForStay onClearFilter={() => handleDatesChange(null, null)} />
      )}

      {/* Experience List */}
      <ExperienceList 
        experiences={experiences} 
        isLoading={isLoading}
        emptyMessage={
          isFilteredByStay 
            ? "No experiences available during your stay dates" 
            : undefined
        }
      />

      {/* Filtered results indicator */}
      {isFilteredByStay && experiences.length > 0 && (
        <FilteredResultsFooter 
          count={experiences.length}
          daysLeft={daysUntilStayEnds}
        />
      )}
    </EmbedLayout>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
      <p className="text-destructive">{message}</p>
    </div>
  );
}

interface NoExperiencesForStayProps {
  onClearFilter: () => void;
}

function NoExperiencesForStay({ onClearFilter }: NoExperiencesForStayProps) {
  return (
    <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-8 text-center">
      <div className="mx-auto max-w-md">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No experiences available for your dates
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Unfortunately, there are no experiences with availability during your stay. 
          Try adjusting your dates or browse all experiences.
        </p>
        <button
          onClick={onClearFilter}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Browse all experiences
        </button>
      </div>
    </div>
  );
}

interface FilteredResultsFooterProps {
  count: number;
  daysLeft: number | null;
}

function FilteredResultsFooter({ count, daysLeft }: FilteredResultsFooterProps) {
  return (
    <div className="mt-6 text-center">
      <p className="text-sm text-muted-foreground">
        Showing {count} experience{count !== 1 ? 's' : ''} available during your stay
        {daysLeft !== null && daysLeft <= 7 && (
          <span className="ml-1 font-medium text-primary">
            • {daysLeft === 0 ? 'Book today!' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
          </span>
        )}
      </p>
    </div>
  );
}
